import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Paperclip, X } from "@phosphor-icons/react";

// Multi-round file picker. Exposes `.files` and `.value = ""` (clear) like a native file input ref.
const FilePicker = forwardRef(function FilePicker({ testid, label = "Add files", className = "" }, ref) {
  const [picked, setPicked] = useState([]);
  const inputRef = useRef(null);
  useImperativeHandle(ref, () => ({
    get files() { return picked; },
    set value(v) { if (v === "") setPicked([]); },
  }), [picked]);
  const add = (list) => {
    // Snapshot the live FileList now: the input is cleared right after, before a deferred updater runs.
    const incoming = Array.from(list || []);
    setPicked(prev => {
      const out = [...prev];
      for (const f of incoming) {
        if (!out.some(p => p.name === f.name && p.size === f.size && p.lastModified === f.lastModified)) out.push(f);
      }
      return out;
    });
  };
  return (
    <div className={`flex flex-wrap items-center gap-2 min-w-0 ${className}`} data-testid={testid ? `${testid}-picker` : undefined}>
      <input ref={inputRef} type="file" multiple className="hidden" data-testid={testid}
        onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      <button type="button" onClick={() => inputRef.current?.click()}
        data-testid={testid ? `${testid}-add` : undefined} className="btn-ghost text-xs h-9">
        <Paperclip size={12} weight="bold" /> {picked.length ? "Add more files" : label}
      </button>
      {picked.length > 0 && (
        <span className="label" data-testid={testid ? `${testid}-count` : undefined}>
          {picked.length} file{picked.length === 1 ? "" : "s"}
        </span>
      )}
      {picked.map((f, i) => (
        <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs hair max-w-[220px]">
          <span className="truncate">{f.name}</span>
          <button type="button" onClick={() => setPicked(picked.filter((_, j) => j !== i))}
            data-testid={testid ? `${testid}-remove-${i}` : undefined}
            aria-label={`Remove ${f.name}`} className="shrink-0 -m-1 p-1.5"><X size={12} weight="bold" /></button>
        </span>
      ))}
    </div>
  );
});

export default FilePicker;
